// ============================================
// components/Homepage.js - Updated with Settings button
// ============================================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Typography, Box, IconButton, Popover, Button } from '@mui/material';

import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';

// Hooks
import { useAuth } from '../hooks/useAuth';
import { usePorchlight } from '../hooks/usePorchlight';
import { useFriends } from '../hooks/useFriends';
import { useNotifications } from '../hooks/useNotifications';
// Anchor glow height to Friends section
import { useGlowAnchoredTo } from '../hooks/useGlowAnchoredTo';

// Components
import PorchlightLamp from './porchlight/PorchlightLamp';
import PorchlightControls from './porchlight/PorchlightControls';
import PorchlightSettingsModal from './porchlight/PorchlightSettingsModal';
import FriendsList from './friends/FriendsList';
import FriendRequests from './friends/FriendRequests';
import AddFriend from './friends/AddFriend';
import NotificationAlert from './common/NotificationAlert';
import EmailVerificationWarning from './common/EmailVerificationWarning';
import LoadingSpinner from './common/LoadingSpinner';

// Utils & Constants
import theme from '../styles/theme';
import { MESSAGES, ERRORS, ROUTES } from '../utils/constants';

const Homepage = () => {
  const navigate = useNavigate();
  
  // Custom hooks
  const { user, userData, loading: authLoading, emailVerified, refreshAuthUser } = useAuth();
  const { isActive, updatePorchlight } = usePorchlight(user?.uid);
  const { 
    friends, 
    friendRequests, 
    sendFriendRequest, 
    acceptRequest, 
    denyRequest,
    loadingFriends,
    loadingRequests 
  } = useFriends(user?.uid);
  const { 
    notification, 
    setNotification, 
    permission: notificationPermission,
    requestPermission 
  } = useNotifications(user?.uid);
  
  // Local state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [pendingOn, setPendingOn] = useState(false);
  const [error, setError] = useState('');
  const addFriendRef = useRef(null);
  const overlayHostRef = useRef(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  // Anchor the lamp glow height to the bottom of the Friends section
  // This dynamically sets --glow-height-used for CSS to consume.
  useGlowAnchoredTo('#friends-section', { extra: 2, min: 10, max: 600 });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Show loading spinner while auth is loading
  if (authLoading) {
    return <LoadingSpinner fullScreen />;
  }

  const handleLampClick = () => {
    // Offline guard: do not open modal when offline
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setError(ERRORS.OFFLINE);
      return;
    }

    if (!emailVerified) {
      setError(ERRORS.EMAIL_NOT_VERIFIED);
      return;
    }

    // Soft haptic feedback on supported mobile devices
    try {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(12);
      }
    } catch (_) {}

    if (isActive) {
      // Turn off directly
      handleUpdatePorchlight(0, []);
    } else {
      // Show settings modal for turning on
      setPendingOn(true);
      setShowSettingsModal(true);
    }
  };

  const handleUpdatePorchlight = async (minutes, selectedFriends) => {
    const result = await updatePorchlight(minutes, selectedFriends);
    
    if (result.success) {
      setShowSettingsModal(false);
      setPendingOn(false);
      // Align success text with Cypress spec: "Porchlight is on"
      const message = minutes === 0 
        ? MESSAGES.PORCHLIGHT_OFF 
        : MESSAGES.PORCHLIGHT_ON(minutes);
      setNotification({
        open: true,
        message,
        title: 'Success'
      });
    } else {
      setError(result.error);
      setPendingOn(false);
    }
  };

  const handleAddFriend = async (email) => {
    if (!emailVerified) {
      setError(ERRORS.EMAIL_NOT_VERIFIED);
      return { success: false };
    }
    
    const result = await sendFriendRequest(email);
    if (result.success) {
      setNotification({ 
        open: true, 
        message: MESSAGES.FRIEND_REQUEST_SENT, 
        title: 'Success' 
      });
    } else {
      setError(result.error);
    }
    return result;
  };

  const handleAcceptRequest = async (requestId) => {
    const result = await acceptRequest(requestId);
    if (result.success) {
      setNotification({ 
        open: true, 
        message: MESSAGES.FRIEND_REQUEST_ACCEPTED, 
        title: 'Success' 
      });
    } else {
      setError(result.error);
    }
    return result;
  };

  const handleDenyRequest = async (requestId) => {
    const result = await denyRequest(requestId);
    if (result.success) {
      setNotification({ 
        open: true, 
        message: MESSAGES.FRIEND_REQUEST_DENIED, 
        title: 'Done' 
      });
    } else {
      setError(result.error);
    }
    return result;
  };

  const handleRequestNotifications = async () => {
    const result = await requestPermission();
    if (!result.success) {
      setError(result.error || 'Error enabling notifications');
    }
  };

  const handleAdjustSettings = () => {
    if (!emailVerified) {
      setError(ERRORS.EMAIL_NOT_VERIFIED);
      return;
    }
    setShowSettingsModal(true);
  };

  const handleModalClose = () => {
    setShowSettingsModal(false);
    setPendingOn(false);
  };

  const handleVerificationResent = () => {
    setNotification({ 
      open: true, 
      message: MESSAGES.EMAIL_VERIFICATION_SENT, 
      title: 'Email Sent' 
    });
  };

  const displayActive = pendingOn || isActive;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main className="app-container" aria-labelledby="page-title">
        <Box component="h1" id="page-title" sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1
        }}>
          Porchlight
        </Box>
        {/* Settings Gear (no AppBar) */}
        <Box sx={{ position: 'fixed', top: 'calc(12px + env(safe-area-inset-top))', right: 8, zIndex: (theme) => theme.zIndex.tooltip }}>
          <IconButton
            aria-label="Open settings"
            data-testid="settings-button"
            edge="end"
            color="inherit"
            onClick={() => navigate(ROUTES.SETTINGS)}
          >
            <SettingsIcon />
          </IconButton>
        </Box>

        {/* Notification bell (shows until permission granted) */}
        {notificationPermission === 'default' && (
          <Box sx={{ position: 'fixed', top: 'calc(12px + env(safe-area-inset-top))', left: 8, zIndex: (theme) => theme.zIndex.tooltip }}>
            <IconButton
              aria-label="Enable notifications"
              data-testid="enable-notifications-btn"
              edge="start"
              color="inherit"
              onClick={(e) => setNotifAnchorEl(e.currentTarget)}
            >
              <NotificationsIcon />
            </IconButton>
          </Box>
        )}
        <Popover
          open={Boolean(notifAnchorEl)}
          anchorEl={notifAnchorEl}
          onClose={() => setNotifAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              p: 2,
              backgroundColor: (theme) => theme.palette.background.paper,
              border: '1px solid rgba(255,255,255,0.16)',
              borderRadius: 2,
              maxWidth: 340,
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
            },
            elevation: 6
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Get notified when friends invite you or when your porchlight status changes.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              data-testid="popover-enable-notifications-btn"
              onClick={async () => {
                await handleRequestNotifications();
                setNotifAnchorEl(null);
              }}
            >
              Enable notifications
            </Button>
          </Box>
        </Popover>

        {/* Email Verification Warning */}
        <Box
          sx={{
            position: 'relative',
            zIndex: (theme) => theme.zIndex.appBar, // ensure above lamp/button
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            pointerEvents: 'auto',
          }}
        >
          <EmailVerificationWarning 
            show={!emailVerified}
            onResendSuccess={handleVerificationResent}
            onCheckNow={refreshAuthUser}
          />
        </Box>

        {/* Global glow host for lamp overlay */}
        <div ref={overlayHostRef} className="glow-host" aria-hidden="true" />

        {/* Main Porchlight */}
        <div className="porchlight-lamp-container">
          <PorchlightLamp 
            isActive={displayActive}
            onClick={handleLampClick}
            overlayHostEl={overlayHostRef.current}
          />
        </div>
        
        <div className="porchlight-controls-container">
          <PorchlightControls
            isActive={displayActive}
            isPending={pendingOn}
            onAdjustSettings={handleAdjustSettings}
          />
        </div>

        {/* Notification Permission CTA removed in favor of top-left bell */}

        {/* Friend Requests */}
        <FriendRequests
          requests={friendRequests}
          currentUserId={user?.uid}
          onAccept={handleAcceptRequest}
          onDeny={handleDenyRequest}
          isLightOn={displayActive}
        />

        {/* Friends List */}
        <div id="friends-section" className="friends-info-container">
          <FriendsList 
            friends={friends} 
            isLightOn={displayActive}
            loading={loadingFriends}
          />
        </div>

        {/* Inline Add Friend form */}
        <Box ref={addFriendRef} sx={{ width: '100%', maxWidth: 420, px: 2, mx: 'auto', mb: 2, display: 'flex', justifyContent: 'center' }}>
          <AddFriend onAddFriend={handleAddFriend} disabled={!emailVerified} defaultOpen={true} />
        </Box>

        {/* Settings Modal */}
        <PorchlightSettingsModal
          open={showSettingsModal}
          onClose={handleModalClose}
          friends={friends}
          onConfirm={handleUpdatePorchlight}
          isNewSession={pendingOn}
        />

        {/* Success Notifications */}
        <NotificationAlert
          notification={notification}
          onClose={() => setNotification({ ...notification, open: false })}
          // Keep visible longer during E2E assertions
          autoHideDuration={8000}
          dataTestId="notification-alert"
        />
        
        {/* Error Notifications */}
        <NotificationAlert
          notification={{ open: !!error, message: error, title: 'Error' }}
          severity="error"
          onClose={() => setError('')}
          autoHideDuration={5000}
          dataTestId="error-notification-alert"
          />
      </main>
    </ThemeProvider>
  );
};

export default Homepage;